import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import {
  FILTER_TYPES,
  FilterItem,
  TagFilterItem,
} from '@mm/state/model/AudiencesModel';

export function enumerateKeys<T>(obj: Record<string, any>, startFrom = 0) {
  if (typeof obj !== 'object') return obj;
  return Object.values(obj).reduce((a, v, i) => {
    return { ...a, [startFrom + i]: v };
  }, {} as Record<string, any>);
}

const defaultFilterParams = { logic: '' };

const mapFilterItemToExpectedModel = (item: FilterItem) => {
  const { variableId, variableType, operator, value, filterType } = item;
  return FILTER_TYPES.variableValues
    ? {
        variableId,
        variableType,
        operator,
        value,
        filterType,
      }
    : {
        value,
        filterType,
      };
};

export const useAudienceFiltersHandlers = (audienceId: number) => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId),
  );
  const { update: updateAudience } = useAudiencesCRUDActions();

  const { filterParams = defaultFilterParams, tagsFilters } = audience || {};

  const addFilter = (filter?: FilterItem) => {
    if (filter) {
      const { logic, ...clone } = cloneDeep(filterParams);
      const { length } = Object.keys(clone);
      clone[length + 1] = mapFilterItemToExpectedModel(filter);
      updateAudience(
        { id: audienceId, filterParams: { ...clone, logic } },
        activeChatbotId,
      );
    }
  };

  const addTagFilter = (tagFilterItem: TagFilterItem) => {
    if (tagFilterItem) {
      const clone = cloneDeep(tagsFilters) || {};
      const keys = Object.keys(clone);
      clone[keys.length + 1] = tagFilterItem;
      updateAudience({ id: audienceId, tagsFilters: clone }, activeChatbotId);
    }
  };

  const updateFilterItemById = (
    filterId: number,
    updatedFilter: TagFilterItem,
  ) => {
    if (filterId && updatedFilter) {
      const clonedFilterParams = cloneDeep(filterParams);
      clonedFilterParams[filterId] = updatedFilter;

      updateAudience(
        { id: audienceId, filterParams: clonedFilterParams },
        activeChatbotId,
      );
    }
  };

  const updateTagFilterItemById = (
    filterId: number,
    updatedTag: TagFilterItem,
  ) => {
    if (filterId && updatedTag) {
      const clonedTagsFilters = cloneDeep(tagsFilters) || {};
      clonedTagsFilters[filterId] = updatedTag;
      updateAudience(
        {
          id: audienceId,
          tagsFilters: clonedTagsFilters,
        },
        activeChatbotId,
      );
    }
  };

  const deleteFilterItemById = async (filterId: number) => {
    if (filterParams && filterId) {
      const { logic, ...clone } = cloneDeep(filterParams);
      delete clone[filterId];
      const enumerated = enumerateKeys(clone, 1);

      await updateAudience(
        {
          id: audienceId,
          filterParams: {
            ...enumerated,
            logic,
          },
        },
        activeChatbotId,
      );
    }
  };

  const deleteTagFilterItemById = async (filterId: number) => {
    if (tagsFilters && filterId) {
      let clonedTagsFilters = cloneDeep(tagsFilters);
      delete clonedTagsFilters[filterId];
      clonedTagsFilters = enumerateKeys(clonedTagsFilters, 1);
      await updateAudience(
        {
          id: audienceId,
          tagsFilters: clonedTagsFilters,
        },
        activeChatbotId,
      );
    }
  };

  return {
    addFilter,
    addTagFilter,
    deleteFilterItemById,
    deleteTagFilterItemById,
    updateFilterItemById,
    updateTagFilterItemById,
  };
};
